import { auth } from "./firebase";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export interface VisitNote {
  id: string;
  patientName: string;
  doctorName: string;
  visitDate: string;
  diagnosis: string | null;
  notes: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface VisitNoteInput {
  patientName: string;
  doctorName: string;
  visitDate: string;
  diagnosis?: string;
  notes: string;
}

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const idToken = await auth.currentUser?.getIdToken();

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      ...init.headers,
    },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({ message: response.statusText }));
    throw new ApiError(response.status, body.message ?? "Request failed");
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export const visitNotesApi = {
  list: (params: { limit?: number; offset?: number } = {}) => {
    const search = new URLSearchParams();
    if (params.limit) search.set("limit", String(params.limit));
    if (params.offset) search.set("offset", String(params.offset));
    const qs = search.toString();
    return request<{ items: VisitNote[]; limit: number; offset: number }>(
      `/visit-notes${qs ? `?${qs}` : ""}`,
    );
  },
  get: (id: string) => request<VisitNote>(`/visit-notes/${id}`),
  create: (input: VisitNoteInput) =>
    request<VisitNote>("/visit-notes", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  update: (id: string, input: Partial<VisitNoteInput>) =>
    request<VisitNote>(`/visit-notes/${id}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  remove: (id: string) =>
    request<void>(`/visit-notes/${id}`, { method: "DELETE" }),
};
